// src/components/dashboard/StatsCards/StatsCards.jsx
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Alert,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AttachMoney,
  People,
  SportsEsports,
  EmojiEvents,
  Analytics,
  TrendingUp,
  Casino,
  Percent,
  MonetizationOn,
  PersonAdd,
  Group,
  Savings,
} from '@mui/icons-material';

import StatCard from './StatCard'; // Assuming StatCard is a separate, complex component
import {
  selectDashboardStats,
  selectDashboardError,
  selectDataFreshness,
  selectErrorStates,
} from '../../../store/slices/dashboardSlice';
import { useLazyGetMetricsQuery } from '../../../store/api/dashboardApi';
import { USER_ROLES } from '../../../utils/constants';

/* -------------------------------------------------------------------------- */
/* CONFIGURATION                                */
/* -------------------------------------------------------------------------- */
const getCardConfig = (isSuperAdmin, customerPeriodLabel) => [
  { id: 'totalPlay', title: 'Total Play', icon: AttachMoney, gradient: 'primary', priority: 1, isCurrency: true },
  ...(isSuperAdmin
    ? [
        { id: 'grossRevenue', title: 'Gross Revenue', icon: Analytics, gradient: 'warning', priority: 2, isCurrency: true },
        { id: 'netRevenue', title: 'Net Revenue', icon: Savings, gradient: 'success', priority: 3, isCurrency: true },
        { id: 'jackpotContribution', title: 'Jackpot Contribution', icon: EmojiEvents, gradient: 'purple', priority: 4, isCurrency: true },
        { id: 'grossMargin', title: 'Gross Margin', icon: Percent, gradient: 'warning', priority: 5, suffix: '%' },
      ]
    : [
        { id: 'revenue', title: 'Revenue', icon: Analytics, gradient: 'warning', priority: 2, isCurrency: true },
      ]),
  { id: 'totalWin', title: 'Total Winnings', icon: MonetizationOn, gradient: 'info', priority: isSuperAdmin ? 6 : 3, isCurrency: true },
  { id: 'customers', title: 'Active Customers', icon: People, gradient: 'info', priority: isSuperAdmin ? 7 : 4 },
  { id: 'newCustomers', title: `New ${customerPeriodLabel}`, icon: PersonAdd, gradient: 'success', priority: isSuperAdmin ? 8 : 5 },
  { id: 'existingCustomers', title: `Returning ${customerPeriodLabel}`, icon: Group, gradient: 'primary', priority: isSuperAdmin ? 9 : 6 },
  { id: 'totalBets', title: 'Total Bets', icon: Casino, gradient: 'primary', priority: isSuperAdmin ? 10 : 7 },
  { id: 'winPercentage', title: 'Player Win Rate', icon: Percent, gradient: 'success', priority: isSuperAdmin ? 11 : 8, suffix: '%' },
  { id: 'avgBetAmount', title: 'Average Wager', icon: TrendingUp, gradient: 'info', priority: isSuperAdmin ? 12 : 9, isCurrency: true },
  { id: 'avgWinAmount', title: 'Average Win', icon: EmojiEvents, gradient: 'success', priority: isSuperAdmin ? 13 : 10, isCurrency: true },
  { id: 'profitMargin', title: isSuperAdmin ? 'Net Margin' : 'Profit Margin', icon: Analytics, gradient: 'warning', priority: isSuperAdmin ? 14 : 11, suffix: '%' },
  { id: 'totalGames', title: 'Unique Games', icon: SportsEsports, gradient: 'purple', priority: isSuperAdmin ? 15 : 12 },
];

/* -------------------------------------------------------------------------- */
/* UI HELPER COMPONENTS                           */
/* -------------------------------------------------------------------------- */

const StatsHeader = ({ isLoading, isStale, ageInMinutes, hasErrors, onRefresh }) => (
  <Box mb={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
    <Box display="flex" alignItems="center" gap={1}>
      {!isLoading && ageInMinutes > 0 && (
        <Chip
          size="small"
          icon={isStale ? <WarningIcon /> : <InfoIcon />}
          label={`Updated ${ageInMinutes}m ago`}
          color={isStale ? 'warning' : 'default'}
          variant="outlined"
        />
      )}
      {hasErrors && (
        <Chip size="small" icon={<WarningIcon />} label="Data Issues" color="error" variant="outlined" />
      )}
    </Box>
    <Tooltip title="Refresh Data">
      <span> {/* Span is needed to show tooltip when button is disabled */}
        <IconButton onClick={onRefresh} disabled={isLoading} size="small">
          <RefreshIcon />
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);

const StatsGrid = ({ isLoading, cards }) => {
  if (isLoading) {
    return Array.from(new Array(6)).map((_, idx) => (
      <Box 
        key={idx} 
        sx={{ 
          height: { xs: 130, sm: 145, md: 160, lg: 150 },
          minHeight: { xs: 130, sm: 145, md: 160, lg: 150 },
        }}
      >
        <Skeleton 
          variant="rectangular" 
          height="100%" 
          sx={{ borderRadius: { xs: 2, sm: 2.5, md: 3 } }}
        />
      </Box>
    ));
  }

  return cards.map((card, idx) => (
    <Box key={card.id} sx={{ height: '100%', minHeight: { xs: 130, sm: 145, md: 160, lg: 150 } }}>
      <StatCard {...card} delay={idx * 40} />
    </Box>
  ));
};

const EmptyState = ({ onRefresh }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px" gap={2} p={3}
       sx={{ bgcolor: 'background.paper', borderRadius: 2, mt: 2 }}>
    <Typography variant="h6" color="text.secondary">
      No Statistics Available
    </Typography>
    <Typography variant="body2" color="text.secondary" textAlign="center">
      There’s no data for the selected period. Try adjusting filters or refreshing.
    </Typography>
    <IconButton onClick={onRefresh} color="primary" aria-label="refresh data">
      <RefreshIcon />
    </IconButton>
  </Box>
);

const ErrorAlert = ({ error, onRefresh }) => {
  if (!error) return null;

  return (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={
        <IconButton onClick={onRefresh} size="small" color="inherit" aria-label="refresh on error">
          <RefreshIcon />
        </IconButton>
      }
    >
      <Typography variant="body2">{String(error)}</Typography>
    </Alert>
  );
};

/* -------------------------------------------------------------------------- */
/* CUSTOM DATA HOOK                             */
/* -------------------------------------------------------------------------- */

const useStatsData = () => {
  const stats = useSelector(selectDashboardStats);
  const error = useSelector(selectDashboardError);
  const freshness = useSelector(selectDataFreshness);
  const errorStates = useSelector(selectErrorStates);
  const role = useSelector((state) => state.auth.role);
  const filters = useSelector((state) => state.dashboard.filters);
  const isSuperAdmin = role === USER_ROLES.SUPER_ADMIN;

  const [getMetrics, { isFetching }] = useLazyGetMetricsQuery();

  const derivedStats = useMemo(() => {
    const play = Number(stats.totalPlay) || 0;
    const netRevenue = Number(isSuperAdmin ? (stats.netRevenue ?? stats.revenue) : stats.revenue) || 0;
    const grossRevenue = Number(stats.grossRevenue ?? stats.revenue) || 0;
    return {
      grossMargin: play ? (grossRevenue / play) * 100 : 0,
      profitMargin: play ? (netRevenue / play) * 100 : 0,
    };
  }, [stats, isSuperAdmin]);

  const allStats = useMemo(() => ({ ...stats, ...derivedStats }), [stats, derivedStats]);
  const customerPeriodLabel = useMemo(() => {
    if (filters.dateRange?.start || filters.dateRange?.end) return 'in Range';
    return {
      today: 'Today',
      weekly: 'This Week',
      monthly: 'This Month',
      yearly: 'This Year',
      all_time: 'All Time',
    }[filters.quickFilter?.toLowerCase()] || 'Today';
  }, [filters.dateRange, filters.quickFilter]);
  const cardConfig = useMemo(
    () => getCardConfig(isSuperAdmin, customerPeriodLabel),
    [isSuperAdmin, customerPeriodLabel]
  );

  const cards = useMemo(
    () =>
      cardConfig.map(card => ({
        ...card,
        value: allStats[card.id],
        trend: null,
      })),
    [allStats, cardConfig]
  );

  const visibleCards = useMemo(
    () => cards.filter(card => card.priority <= 6 || (isFinite(Number(card.value)) && Number(card.value) > 0))
               .sort((a, b) => a.priority - b.priority),
    [cards]
  );
  
  const hasInitialData = Object.keys(stats).length > 0;

  return {
    isLoading: isFetching && !hasInitialData, // Show skeleton only on initial load
    isRefreshing: isFetching && hasInitialData, // Use for refresh indicators, not layout shifts
    refetch: () => getMetrics(filters),
    freshness,
    error,
    errorStates,
    cards: visibleCards,
    hasData: visibleCards.length > 0 || hasInitialData,
  };
};

/* -------------------------------------------------------------------------- */
/* MAIN COMPONENT                              */
/* -------------------------------------------------------------------------- */

const StatsCards = () => {
  const { isLoading, isRefreshing, refetch, freshness, error, errorStates, cards, hasData } = useStatsData();
  
  const hasErrors = error || errorStates.metrics;

  return (
    <>
      <StatsHeader
        isLoading={isRefreshing || isLoading}
        isStale={freshness.metrics?.isStale}
        ageInMinutes={freshness.metrics?.ageInMinutes}
        hasErrors={hasErrors}
        onRefresh={refetch}
      />

      <ErrorAlert error={!isLoading && (error || errorStates.metrics)} onRefresh={refetch} />

      {/* Responsive CSS Grid for uniform card sizing - full width on mobile */}
      <Box
        component="div"
        sx={{
          display: 'grid',
          width: '100%',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',           // Mobile: 2 columns
            sm: 'repeat(2, 1fr)',           // Small: 2 columns (larger cards)
            md: 'repeat(3, 1fr)',           // Medium: 3 columns
            lg: 'repeat(4, 1fr)',           // Large: 4 columns  
            xl: 'repeat(6, 1fr)',           // XL: 6 columns (all in one row)
          },
          gap: { 
            xs: 1.5,    // Tighter on mobile
            sm: 2, 
            md: 2.5, 
            lg: 3 
          },
          gridAutoRows: '1fr',              // Equal height rows
          minHeight: { xs: 'auto', md: 180 },
        }}
      >
        <StatsGrid
          isLoading={isLoading}
          cards={cards}
        />
      </Box>

      {!isLoading && !hasData && !hasErrors && (
        <EmptyState onRefresh={refetch} />
      )}
    </>
  );
};

export default StatsCards;

// // src/components/dashboard/StatsCards/StatsCards.jsx
// import React, { useEffect, useMemo, useState } from 'react';
// import {
//   Grid,
//   Container,
//   Box,
//   Typography,
//   Alert,
//   Skeleton,
//   Chip,
//   IconButton,
//   Tooltip,
//   Fade,
// } from '@mui/material';
// import {
//   Refresh as RefreshIcon,
//   Warning as WarningIcon,
//   Info as InfoIcon,
//   AttachMoney,
//   People,
//   SportsEsports,
//   EmojiEvents,
//   Analytics,
//   TrendingUp,
//   Casino,
//   Percent,
//   MonetizationOn,
// } from '@mui/icons-material';

// import { useSelector } from 'react-redux';
// import { useResponsive } from '../../../hooks/useResponsive';
// import StatCard from './StatCard';

// import {
//   selectDashboardStats,
//   selectDashboardError,
//   selectDataFreshness,
//   selectErrorStates,
// } from '../../../store/slices/dashboardSlice';

// import { useGetMetricsQuery } from '../../../store/api/dashboardApi';

// /* ------------------------------------------------------------------ */
// /*  Enhanced Statistics Cards Component (auto-fetch version)           */
// /* ------------------------------------------------------------------ */
// const StatsCards = () => {
//   const stats        = useSelector(selectDashboardStats);
//   const error        = useSelector(selectDashboardError);
//   const freshness    = useSelector(selectDataFreshness);
//   const errorStates  = useSelector(selectErrorStates);

//   /* --------------------------------------------------------------- */
//   /*  RTK-Query: auto-fetch metrics + refetch helper                 */
//   /* --------------------------------------------------------------- */
//   const {
//     isFetching: isLoading,
//     refetch,        // for the manual refresh button
//   } = useGetMetricsQuery({}); // empty params → slice decides filters

//   const { getGridColumns, getSpacing } = useResponsive();

//   /* --------------------------------------------------------------- */
//   /*  Debug info (dev only)                                          */
//   /* --------------------------------------------------------------- */
//   useEffect(() => {
//     if (process.env.NODE_ENV === 'development') {
//       console.group('StatsCards Debug Info');
//       console.log('Stats:', stats);
//       console.log('Loading:', isLoading);
//       console.log('Errors:', { main: error, metric: errorStates.metrics });
//       console.log('Data Freshness:', freshness);
//       console.groupEnd();
//     }
//   }, [stats, isLoading, error, freshness, errorStates]);

//   /* --------------------------------------------------------------- */
//   /*  Helpers                                                        */
//   /* --------------------------------------------------------------- */
//   const calculateTrend = useMemo(
//     () => (current, previous) => {
//       const cur = Number(current);
//       const prev = Number(previous);
//       if (!isFinite(cur) || !isFinite(prev) || prev === 0) return null;
//       const pct = ((cur - prev) / prev) * 100;
//       return {
//         value: Math.abs(pct).toFixed(1),
//         direction: pct >= 0 ? 'up' : 'down',
//         isSignificant: Math.abs(pct) >= 5,
//       };
//     },
//     []
//   );

//   // Mock previous stats (demo only; replace with real history if available)
//   const previousStats = useMemo(() => {
//     const multipliers = {
//       totalPlay: 0.92,
//       totalWin: 0.87,
//       customers: 0.96,
//       revenue: 1.08,
//       totalGames: 0.91,
//       totalBets: 0.89,
//       avgBetAmount: 1.03,
//       avgWinAmount: 0.94,
//       winPercentage: 0.97,
//     };
//     return Object.keys(multipliers).reduce((acc, k) => {
//       acc[k] = (stats[k] || 0) * multipliers[k];
//       return acc;
//     }, {});
//   }, [stats]);

//   // Derived metrics
//   const derived = useMemo(() => {
//     const play = Number(stats.totalPlay) || 0;
//     const win  = Number(stats.totalWin)  || 0;
//     const rev  = Number(stats.revenue)   || 0;
//     return {
//       profitMargin: play ? (rev / play) * 100 : 0,
//     };
//   }, [stats]);

//   /* --------------------------------------------------------------- */
//   /*  Card config                                                    */
//   /* --------------------------------------------------------------- */
//   const cardData = useMemo(
//     () => [
//       {
//         id: 'totalPlay',
//         title: 'Total Play Amount',
//         value: stats.totalPlay,
//         isCurrency: true,
//         icon: AttachMoney,
//         gradient: 'primary',
//         trend: calculateTrend(stats.totalPlay, previousStats.totalPlay),
//         priority: 1,
//       },
//       {
//         id: 'revenue',
//         title: 'Revenue (House Profit)',
//         value: stats.revenue,
//         isCurrency: true,
//         icon: Analytics,
//         gradient: 'warning',
//         trend: calculateTrend(stats.revenue, previousStats.revenue),
//         priority: 2,
//       },
//       {
//         id: 'totalWin',
//         title: 'Total Winnings Paid',
//         value: stats.totalWin,
//         isCurrency: true,
//         icon: EmojiEvents,
//         gradient: 'success',
//         trend: calculateTrend(stats.totalWin, previousStats.totalWin),
//         priority: 3,
//       },
//       {
//         id: 'customers',
//         title: 'Active Customers',
//         value: stats.customers,
//         isCurrency: false,
//         icon: People,
//         gradient: 'info',
//         trend: calculateTrend(stats.customers, previousStats.customers),
//         priority: 4,
//       },
//       {
//         id: 'totalBets',
//         title: 'Total Bets Placed',
//         value: stats.totalBets,
//         isCurrency: false,
//         icon: Casino,
//         gradient: 'primary',
//         trend: calculateTrend(stats.totalBets, previousStats.totalBets),
//         priority: 5,
//       },
//       {
//         id: 'winPercentage',
//         title: 'Player Win Rate',
//         value: stats.winPercentage,
//         isCurrency: false,
//         icon: Percent,
//         gradient: 'success',
//         trend: calculateTrend(stats.winPercentage, previousStats.winPercentage),
//         suffix: '%',
//         priority: 6,
//       },
//       {
//         id: 'avgBetAmount',
//         title: 'Average Bet Size',
//         value: stats.avgBetAmount,
//         isCurrency: true,
//         icon: TrendingUp,
//         gradient: 'info',
//         trend: calculateTrend(stats.avgBetAmount, previousStats.avgBetAmount),
//         priority: 7,
//       },
//       {
//         id: 'avgWinAmount',
//         title: 'Average Win Amount',
//         value: stats.avgWinAmount,
//         isCurrency: true,
//         icon: MonetizationOn,
//         gradient: 'success',
//         trend: calculateTrend(stats.avgWinAmount, previousStats.avgWinAmount),
//         priority: 8,
//       },
//       {
//         id: 'profitMargin',
//         title: 'Profit Margin',
//         value: derived.profitMargin,
//         isCurrency: false,
//         icon: Analytics,
//         gradient: 'warning',
//         suffix: '%',
//         priority: 9,
//       },
//       {
//         id: 'totalGames',
//         title: 'Unique Games',
//         value: stats.totalGames,
//         isCurrency: false,
//         icon: SportsEsports,
//         gradient: 'purple',
//         trend: calculateTrend(stats.totalGames, previousStats.totalGames),
//         priority: 10,
//       },
//     ],
//     [stats, previousStats, calculateTrend, derived]
//   );

//   // Determine which cards to show
//   const visibleCards = useMemo(() => {
//     if (isLoading && Object.keys(stats).length === 0) return cardData.slice(0, 6);
//     return cardData
//       .filter(card =>
//         card.priority <= 6
//           ? true
//           : isFinite(Number(card.value)) && Number(card.value) > 0
//       )
//       .sort((a, b) => a.priority - b.priority);
//   }, [cardData, isLoading, stats]);

//   /* --------------------------------------------------------------- */
//   /*  UI helpers                                                     */
//   /* --------------------------------------------------------------- */
//   const handleRefresh = () => {
//     void refetch();
//   };

//   const columns   = getGridColumns();
//   const itemWidth = Math.floor(12 / Math.min(columns, 5)) || 12;

//   const isDataStale = freshness.metrics?.isStale || false;
//   const dataAge     = freshness.metrics?.ageInMinutes || 0;
//   const hasErrors   = error || errorStates.metrics;

//   /* --------------------------------------------------------------- */
//   /*  Render                                                         */
//   /* --------------------------------------------------------------- */
//   return (
//     <Container maxWidth={false} disableGutters>
//       {/* Status Bar */}
//       <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
//         <Box display="flex" alignItems="center" gap={1}>
//           {!isLoading && dataAge > 0 && (
//             <Chip
//               size="small"
//               icon={isDataStale ? <WarningIcon /> : <InfoIcon />}
//               label={`Updated ${dataAge}m ago`}
//               color={isDataStale ? 'warning' : 'default'}
//               variant="outlined"
//             />
//           )}

//           {hasErrors && (
//             <Chip
//               size="small"
//               icon={<WarningIcon />}
//               label="Data Issues"
//               color="error"
//               variant="outlined"
//             />
//           )}
//         </Box>

//         <Tooltip title="Refresh Data">
//           <IconButton onClick={handleRefresh} disabled={isLoading} size="small">
//             <RefreshIcon />
//           </IconButton>
//         </Tooltip>
//       </Box>

//       {process.env.NODE_ENV === 'development' && (
//         <Fade in timeout={500}>
//           <Alert severity="info" sx={{ mb: 2 }}>
//             <Typography variant="caption">
//               <strong>Dev Debug:</strong> Loading: {isLoading ? 'Yes' : 'No'} • Cards:{' '}
//               {visibleCards.length} • Age: {dataAge}m • Errors: {hasErrors ? 'Yes' : 'No'}
//             </Typography>
//           </Alert>
//         </Fade>
//       )}

//       {hasErrors && !isLoading && (
//         <Alert
//           severity="error"
//           sx={{ mb: 2 }}
//           action={
//             <IconButton onClick={handleRefresh} size="small" color="inherit">
//               <RefreshIcon />
//             </IconButton>
//           }
//         >
//           <Typography variant="body2">
//             {error || errorStates.metrics || 'Failed to load dashboard statistics'}
//           </Typography>
//         </Alert>
//       )}

//       <Grid container spacing={getSpacing(2)} alignItems="stretch">
//         {visibleCards.map((card, idx) => (
//           <Grid item xs={12} sm={6} md={itemWidth} key={card.id} sx={{ display: 'flex' }}>
//             <Box sx={{ flexGrow: 1 }}>
//               {isLoading && Object.keys(stats).length === 0 ? (
//                 <Box sx={{ p: 2, height: '140px' }}>
//                   <Skeleton variant="rectangular" height="100%" />
//                 </Box>
//               ) : (
//                 <StatCard
//                   {...card}
//                   isLoading={isLoading}
//                   delay={idx * 100}
//                   hasError={hasErrors}
//                   isStale={isDataStale}
//                 />
//               )}
//             </Box>
//           </Grid>
//         ))}
//       </Grid>

//       {!isLoading && visibleCards.length === 0 && !hasErrors && (
//         <Box
//           display="flex"
//           flexDirection="column"
//           alignItems="center"
//           justifyContent="center"
//           minHeight="200px"
//           gap={2}
//         >
//           <Typography variant="h6" color="text.secondary">
//             No Statistics Available
//           </Typography>
//           <Typography variant="body2" color="text.secondary" textAlign="center">
//             There’s no data to display for the selected time period.
//             Try adjusting your filters or refreshing the data.
//           </Typography>
//           <IconButton onClick={handleRefresh} color="primary">
//             <RefreshIcon />
//           </IconButton>
//         </Box>
//       )}
//     </Container>
//   );
// };

// export default StatsCards;

